import graphqlResource, { GraphqlResource } from 'modules/core/graphql-resource';
import { Game } from 'modules/games/domain/interfaces/game';
import { GameDto } from 'modules/games/domain/interfaces/game-dto';
import { mapEventTypeForBackend } from 'modules/games/domain/repositories/game-repository';
import { UpsertMatchParams } from 'modules/games/domain/interfaces/upsert-match-params';
import { formatDateWithTimezone } from 'shared/utils';
import { mapGameDtoToGame } from 'modules/games/domain/data-mappers/game.data-mapper';
import { teamByMatchDataMapper } from '../data-mappers/team-by-match.data-mapper';
import { Round } from '../interfaces/round';
import { TeamByMatchId, TeamByMatchIdDto } from '../interfaces/team-by-match-id';

/** Матчи соревнования и туры. */

export type UpsertGameDto = {
  tournamentId: number;
  championshipId: number;
  values: UpsertMatchParams;
  matchId?: number;
};

interface CreateRoundParams {
  name: string;
  stageId: number;
}

interface CreateRoundResponse {
  data: {
    upsertRound: {
      id: number;
      name: string;
      stage: { id: number };
      createdAt: string;
      updatedAt: string;
    };
  };
}

class ChampionshipGameRepository {
  constructor(private readonly graphqlResource: GraphqlResource) {}

  public async upsertGame(params: UpsertGameDto): Promise<Game> {
    const query = `#graphql
      mutation upsertMatch($input: UpsertMatchInput!) {
        upsertMatch(input: $input) {
          id
          sportType
          date
          teams {
            id
            name
            shortName
            emblem {
              id
              externalUrl
            }
          }
          teamMatches {
            team { id }
            score
          }
          matchEvents { id }
          round {
            id
            name
            stage { id }
          }
          location {
            id
            name
            address {
              id
              addressLine1
              addressLine2
              city
              region
              country
              coords {
                lat
                lon
              }
            }
            description
            locationImages {
              image {
                id
                filename
                externalUrl
              }
            }
            tournament {
              id
            }
            createdAt
            updatedAt
          }
        }
      }
    `;

    const { tournamentId, championshipId, values, matchId } = params;

    const [hours, minutes] = values.time.split(':');
    const date = new Date(values.date);
    const gameDate = new Date(date.getFullYear(), date.getMonth(), date.getDate());

    gameDate.setHours(parseInt(hours));
    gameDate.setMinutes(parseInt(minutes));

    const variables = {
      input: {
        matchId,
        tournamentId,
        championshipId,
        sportType: values.sportType,
        firstTeamId: values.firstTeam,
        secondTeamId: values.secondTeam,
        date: formatDateWithTimezone(gameDate),
        roundId: values.roundId ?? undefined,
        locationId: values.locationId,
        firstTeamScore: values.result?.firstTeamScore,
        secondTeamScore: values.result?.secondTeamScore,
        matchEvents: (values.events ?? []).map((event) => ({
          type: mapEventTypeForBackend(event.type),
          firstTeamScore: null,
          secondTeamScore: null,
          playerName: null,
          second: event.seconds,
          period: event.period,
          userId: event.playerIds[0],
          secondaryUserId: event.playerIds[1],
          videoUrl: event?.videoUrl,
        })),
        videoUrls: values.videoUrls,
      },
    };

    if (values.round) {
      const round = await this.createRound(values.round);
      variables.input.roundId = round.id;
    }

    const response = await this.graphqlResource.query<{ data: { upsertMatch: GameDto } }>(query, variables);
    return mapGameDtoToGame(response.data.data.upsertMatch);
  }

  public async createRound(params: CreateRoundParams): Promise<Round> {
    const query = `#graphql
      mutation upsertRound($input: UpsertRoundInput!) {
        upsertRound(input: $input) {
          id
          name
          stage { id }
          createdAt
          updatedAt
        }
      }
    `;

    const variables = {
      input: {
        name: params.name,
        stageId: params.stageId,
      },
    };

    const response = await this.graphqlResource.query<CreateRoundResponse>(query, variables);
    const result = response.data.data.upsertRound;

    return {
      id: result.id,
      name: result.name,
      stageId: result.stage.id,
      createdAt: result.createdAt,
      updatedAt: result.updatedAt,
      matchIds: [],
    };
  }

  public async deleteGame(gameId: number): Promise<number> {
    const query = `#graphql
      mutation deleteMatch($gameId: Int!) {
        deleteMatch(matchId: $gameId) {
          id
        }
      }
    `;

    const response = await this.graphqlResource.query<{ data: { deleteMatch: { id: number } } }>(query, { gameId });
    return response.data.data.deleteMatch.id;
  }

  public async fetchTeamPlayersByMatchId(matchId: number): Promise<TeamByMatchId[]> {
    const query = `#graphql
      query Match($id: Int!) {
        match(id: $id) {
          teams {
            id
            championshipApplications {
              id
              team {
                id
              }
              user {
                id
                name
                surname
                middleName
                photo {
                  id
                  externalUrl
                }
              }
              number
              position
            }
          }
        }
      }
    `;

    const response = await this.graphqlResource.query<TeamByMatchIdDto>(query, { id: matchId });
    return teamByMatchDataMapper(response.data.data.match.teams);
  }
}

const championshipGameRepository = new ChampionshipGameRepository(graphqlResource);

export default championshipGameRepository;
